import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from './Buttons';
import { CustomInput } from './CustomFormElements';
import './ItemsPickerModal.css';
import {
  FetchItemsParams,
  FetchItemsResult,
  getItemId,
  ItemsPickerModalProps,
  PickerColumn,
  PickerItem,
} from './ItemsPickerModalTypes';

function ItemsPickerModal<T extends PickerItem>({
  isOpen,
  onClose,
  title = 'Select items',
  disabled = false,
  items,
  selectedIds = [],
  selectedItems,
  multiSelect = true,
  onSaveSelected,
  saveLabel = 'Save selected',
  searchPlaceholder = 'Search by name...',
  columns,
  onSearch,
  isLoading = false,
  allowEmptySave = false,
  showSearch = true,
  fetchItems,
  perPageOptions = [20, 30, 50, 100, 250, 500],
  defaultPerPage = 100,
  savedItemId = null,
  savedItemUrl = null,
  savedItemLinkLabel = 'Open document',
}: ItemsPickerModalProps<T>) {
  const isPaginated = typeof fetchItems === 'function';
  const isServerSearch = typeof onSearch === 'function';
  const resolvedColumns = useMemo(() => columns ?? [], [columns]);
  const staticItems = useMemo(() => items ?? [], [items]);
  const preselectedItems = useMemo(() => selectedItems ?? [], [selectedItems]);

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [selected, setSelected] = useState<Record<string, T>>({});

  // Paginated-mode state
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(defaultPerPage);
  const [pages, setPages] = useState<Record<number, T[]>>({});
  const [pageTokens, setPageTokens] = useState<Record<number, string | null>>({ 1: null });
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const safeFetch = useCallback(
    async (params: FetchItemsParams): Promise<FetchItemsResult<T>> => {
      if (!fetchItems) return { items: [], nextPageToken: null, hasMore: false };
      try {
        const result = await fetchItems(params);
        if (result && Array.isArray(result.items)) return result;
        return { items: [], nextPageToken: null, hasMore: false };
      } catch (e) {
        console.error('fetchItems error:', e);
        return { items: [], nextPageToken: null, hasMore: false };
      }
    },
    [fetchItems],
  );

  const applyAutoSelect = useCallback(
    (loaded: T[]) => {
      if (!selectedIds || selectedIds.length === 0) return;
      setSelected((prev) => {
        const next = { ...prev };
        for (const id of selectedIds) {
          const key = String(id);
          if (next[key]) continue;
          const match = loaded.find((it) => getItemId(it) === key);
          if (match) next[key] = match;
        }
        return next;
      });
    },
    [selectedIds],
  );

  // Seed the selection map from whichever full objects we already hold, so the
  // Save payload keeps ids whose row is never rendered during this session.
  const buildInitialSelection = useCallback(
    (pool: T[]) => {
      const initial: Record<string, T> = {};
      for (const id of selectedIds || []) {
        const key = String(id);
        if (initial[key]) continue;
        const match = pool.find((it) => getItemId(it) === key);
        if (match) initial[key] = match;
      }
      return initial;
    },
    [selectedIds],
  );

  const loadFirstPage = useCallback(
    async (searchQuery: string | null, limit: number) => {
      setIsFetching(true);
      const res = await safeFetch({ limit, pageToken: null, searchQuery });
      setPages({ 1: res.items });
      setPageTokens({ 1: null, 2: res.nextPageToken });
      setPage(1);
      setIsFetching(false);
      applyAutoSelect(res.items);
    },
    [safeFetch, applyAutoSelect],
  );

  // Debounced search input
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 700);
    return () => clearTimeout(t);
  }, [search]);

  // Reset transient state on each open. The fetch effect below handles the
  // initial fetch (and every subsequent search/perPage change) uniformly, so
  // we do not need a skip-ref dance to suppress a "first fire".
  useEffect(() => {
    if (!isOpen) return;

    setSearch('');
    setDebouncedSearch('');

    if (isPaginated) {
      setPage(1);
      setPerPage(defaultPerPage);
      setPages({});
      setPageTokens({ 1: null });
      setSelected(buildInitialSelection(preselectedItems));
    } else {
      setSelected(buildInitialSelection([...preselectedItems, ...staticItems]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fetch on open and on every search / perPage change while open.
  useEffect(() => {
    if (!isOpen) return;
    if (isPaginated) {
      loadFirstPage(debouncedSearch || null, perPage);
    } else if (isServerSearch) {
      onSearch!(debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, debouncedSearch, perPage]);

  const ensurePageLoaded = useCallback(
    async (targetPage: number) => {
      if (pages[targetPage]) return true;
      const token = pageTokens[targetPage];
      if (!token) return false;
      setIsFetching(true);
      const res = await safeFetch({
        limit: perPage,
        pageToken: token,
        searchQuery: debouncedSearch || null,
      });
      setPages((prev) => ({ ...prev, [targetPage]: res.items }));
      setPageTokens((prev) => ({ ...prev, [targetPage + 1]: res.nextPageToken }));
      setIsFetching(false);
      applyAutoSelect(res.items);
      return true;
    },
    [pages, pageTokens, perPage, debouncedSearch, safeFetch, applyAutoSelect],
  );

  const goNext = useCallback(async () => {
    const target = page + 1;
    const ok = await ensurePageLoaded(target);
    if (ok) setPage(target);
  }, [page, ensurePageLoaded]);

  const goPrev = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const pageItems: T[] = useMemo(
    () => (isPaginated ? (pages[page] ?? []) : staticItems),
    [isPaginated, pages, page, staticItems],
  );

  const filteredItems = useMemo(() => {
    if (isPaginated) return pageItems;
    if (isServerSearch) return pageItems;

    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return pageItems;

    const searchableColumns = resolvedColumns.filter(
      (c) => c.searchable && typeof c.getSearchValue === 'function',
    );

    if (searchableColumns.length === 0) {
      return pageItems.filter((it) =>
        String(it.name || '')
          .toLowerCase()
          .includes(q),
      );
    }

    return pageItems.filter((it) =>
      searchableColumns.some((col) =>
        String(col.getSearchValue!(it) || '')
          .toLowerCase()
          .includes(q),
      ),
    );
  }, [isPaginated, pageItems, debouncedSearch, resolvedColumns, isServerSearch]);

  const selectedCount = Object.keys(selected).length;
  const filteredCount = filteredItems.length;
  const totalCount = isPaginated ? pageItems.length : staticItems.length;
  const showEmpty = !isFetching && filteredCount === 0;

  const rangeStart = pageItems.length === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = pageItems.length === 0 ? 0 : (page - 1) * perPage + pageItems.length;
  const canGoPrev = page > 1 && !isFetching;
  const canGoNext = !isFetching && (Boolean(pages[page + 1]) || Boolean(pageTokens[page + 1]));

  // Select-all acts on the rows currently rendered (this page, after any
  // client-side filtering) and leaves selections made on other pages alone.
  const pageRowIds = useMemo(
    () => filteredItems.map((it) => getItemId(it)).filter(Boolean),
    [filteredItems],
  );
  const selectedOnPageCount = useMemo(
    () => pageRowIds.filter((id) => Boolean(selected[id])).length,
    [pageRowIds, selected],
  );
  const allPageSelected = pageRowIds.length > 0 && selectedOnPageCount === pageRowIds.length;
  const somePageSelected = selectedOnPageCount > 0 && !allPageSelected;

  const toggleAllOnPage = useCallback(() => {
    setSelected((prev) => {
      const next = { ...prev };
      for (const item of filteredItems) {
        const key = getItemId(item);
        if (!key) continue;
        if (allPageSelected) {
          delete next[key];
        } else {
          next[key] = item;
        }
      }
      return next;
    });
  }, [filteredItems, allPageSelected]);

  const toggleOne = useCallback(
    (item: T) => {
      const key = getItemId(item);
      setSelected((prev) => {
        const isSelected = Boolean(prev[key]);
        if (multiSelect) {
          const next = { ...prev };
          if (isSelected) {
            delete next[key];
          } else {
            next[key] = item;
          }
          return next;
        }
        return isSelected ? {} : ({ [key]: item } as Record<string, T>);
      });
    },
    [multiSelect],
  );

  const handleSearchChange = useCallback((_id: string, value: string) => {
    setSearch(value);
  }, []);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose?.();
    },
    [onClose],
  );

  const handleSaveSelected = useCallback(() => {
    onSaveSelected(Object.values(selected));
  }, [onSaveSelected, selected]);

  const handlePerPageChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setPerPage(Number(e.target.value));
    setPage(1);
  }, []);

  const openSavedDocument = useCallback(() => {
    if (savedItemUrl) window.open(savedItemUrl, '_blank', 'noreferrer');
  }, [savedItemUrl]);

  if (!isOpen) return null;

  return (
    <div className="items-picker-overlay" onClick={handleOverlayClick}>
      <div className="items-picker-dialog radius-yes bg-yes p-2">
        <div className="items-picker-header d-flex justify-between align-center">
          <div>
            <h3 className="items-picker-title h3-title">{title}</h3>
            <div className="items-picker-meta small-font">
              {isPaginated ? (
                <>
                  Showing:{' '}
                  <b>
                    {rangeStart}-{rangeEnd}
                  </b>{' '}
                  • Selected: <b>{selectedCount}</b>
                  {savedItemId && (
                    <>
                      {' '}
                      • Saved
                      {savedItemUrl && (
                        <>
                          {' '}
                          •{' '}
                          <span
                            className="items-picker-saved-link"
                            onClick={openSavedDocument}
                            title="Open saved document"
                          >
                            {savedItemLinkLabel}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </>
              ) : (
                <>
                  Showing: <b>{filteredCount}</b> of <b>{totalCount}</b> • Selected:{' '}
                  <b>{selectedCount}</b>
                </>
              )}
              {(isLoading || isFetching) && (
                <span className="items-picker-loading">Loading...</span>
              )}
            </div>
          </div>
        </div>

        <div className="items-picker-body border-yes radius-yes p-2">
          {showSearch && (
            <div className="items-picker-toolbar d-flex justify-between align-center">
              <div className="items-picker-search">
                <CustomInput
                  id="items-picker-search"
                  label="Search"
                  placeholder={searchPlaceholder}
                  type="text"
                  value={search}
                  onChange={handleSearchChange}
                  disabled={disabled || isFetching}
                />
              </div>
              <div />
            </div>
          )}

          <div className="items-picker-list border-yes radius-yes">
            <div className="items-picker-list-header d-flex">
              <div className="items-picker-col-checkbox">
                {multiSelect && (
                  <input
                    type="checkbox"
                    aria-label={
                      allPageSelected ? 'Deselect all on this page' : 'Select all on this page'
                    }
                    title={
                      allPageSelected ? 'Deselect all on this page' : 'Select all on this page'
                    }
                    checked={allPageSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = somePageSelected;
                    }}
                    onChange={toggleAllOnPage}
                    disabled={disabled || isFetching || pageRowIds.length === 0}
                  />
                )}
              </div>
              {resolvedColumns.map((col) => (
                <div
                  key={col.key}
                  className={`items-picker-col ${col.widthClass ?? 'items-picker-col--flex-1'}`}
                >
                  {col.header}
                </div>
              ))}
            </div>

            {showEmpty ? (
              <div className="items-picker-empty small-font">No items found</div>
            ) : (
              filteredItems.map((it) => (
                <ItemRow
                  key={getItemId(it)}
                  item={it}
                  columns={resolvedColumns}
                  checked={Boolean(selected[getItemId(it)])}
                  disabled={disabled}
                  isSaved={Boolean(savedItemId) && getItemId(it) === String(savedItemId)}
                  onToggle={toggleOne}
                />
              ))
            )}
          </div>
        </div>

        {isPaginated ? (
          <div className="items-picker-footer items-picker-footer--paginated">
            <div className="items-picker-footer-left d-flex align-center">
              <div className="items-picker-footer-label small-font">Rows per page:</div>
              <select
                className="items-picker-per-page-select"
                value={perPage}
                onChange={handlePerPageChange}
                disabled={isFetching}
                title="Rows per page"
              >
                {perPageOptions.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>

            <div className="items-picker-footer-center small-font">
              Page <b>{page}</b>
            </div>

            <div className="items-picker-footer-right d-flex align-center">
              <div className="items-picker-pagination d-flex align-center">
                <button
                  type="button"
                  className="items-picker-icon-btn"
                  title="Previous page"
                  onClick={goPrev}
                  disabled={!canGoPrev}
                >
                  &lt;
                </button>
                <button
                  type="button"
                  className="items-picker-icon-btn"
                  title="Next page"
                  onClick={goNext}
                  disabled={!canGoNext}
                >
                  &gt;
                </button>
              </div>

              <div className="items-picker-divider" />

              <Button variant="danger" size="medium" onClick={onClose} disabled={isFetching}>
                Close
              </Button>
              <Button
                variant="primary"
                size="medium"
                onClick={handleSaveSelected}
                disabled={disabled || isFetching || (!allowEmptySave && selectedCount === 0)}
              >
                {saveLabel}
              </Button>
            </div>
          </div>
        ) : (
          <div className="items-picker-footer">
            <Button variant="danger" size="medium" onClick={onClose}>
              Close
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleSaveSelected}
              disabled={disabled || (!allowEmptySave && selectedCount === 0)}
            >
              {saveLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

interface ItemRowProps<T extends PickerItem> {
  item: T;
  columns: PickerColumn<T>[];
  checked: boolean;
  disabled: boolean;
  isSaved?: boolean;
  onToggle: (item: T) => void;
}

function ItemRow<T extends PickerItem>({
  item,
  columns,
  checked,
  disabled,
  isSaved = false,
  onToggle,
}: ItemRowProps<T>) {
  const handleChange = useCallback(() => {
    onToggle(item);
  }, [onToggle, item]);

  const rowClassName = [
    'items-picker-row d-flex align-center',
    isSaved ? 'items-picker-row--saved' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rowClassName}>
      <div className="items-picker-col-checkbox">
        <input type="checkbox" checked={checked} onChange={handleChange} disabled={disabled} />
      </div>

      {columns.map((col) => (
        <div
          key={col.key}
          className={`items-picker-col ${col.widthClass ?? 'items-picker-col--flex-1'}`}
        >
          {col.render(item)}
        </div>
      ))}
    </div>
  );
}

export default ItemsPickerModal;
